import { ArrowDownUp, Copy, Dices } from "lucide-react";

export type SelectionToolsButtonsProps = {
  orientation: "horizontal" | "vertical";
  canFillTop: boolean;
  canRamp: boolean;
  /** False when there is no multi-row selection. */
  canRandomHex: boolean;
  onFillTop: () => void;
  onRamp: () => void;
  onRandomHex: () => void;
};

const btnClass =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border border-transparent text-slate-100 hover:border-lab-line hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-slate-100";

const iconStyle = { width: 18, height: 18 } as const;

/**
 * Fill / ramp / random hex tools — shared by the floating toolbar and left rail.
 */
export function SelectionToolsButtons({
  orientation,
  canFillTop,
  canRamp,
  canRandomHex,
  onFillTop,
  onRamp,
  onRandomHex,
}: SelectionToolsButtonsProps) {
  return (
    <div
      data-selection-toolbar=""
      className={
        orientation === "vertical"
          ? "flex flex-col items-center gap-0.5"
          : "flex items-center gap-0.5"
      }
      role="toolbar"
      aria-label="Selection tools"
      onMouseDown={event => {
        // Keep tracker focus/selection; don't let the button steal focus.
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <button
        type="button"
        title="Fill with top row"
        aria-label="Fill with top row"
        className={btnClass}
        disabled={!canFillTop}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onFillTop();
        }}
      >
        <Copy style={iconStyle} strokeWidth={2.25} aria-hidden />
      </button>
      <button
        type="button"
        title={
          canRamp
            ? "Auto-ramp (lerp first → last hex)"
            : "Auto-ramp needs hex values on the first and last rows (same track)"
        }
        aria-label="Auto-ramp"
        className={btnClass}
        disabled={!canRamp}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onRamp();
        }}
      >
        <ArrowDownUp style={iconStyle} strokeWidth={2.25} aria-hidden />
      </button>
      <button
        type="button"
        title="Fill with random hex"
        aria-label="Fill with random hex"
        className={btnClass}
        disabled={!canRandomHex}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onRandomHex();
        }}
      >
        <Dices style={iconStyle} strokeWidth={2.25} aria-hidden />
      </button>
    </div>
  );
}
